import React, { useState, useEffect, useMemo } from 'react';
import { auth, db } from '../../../../../../firebase/config';
import Modal from '../../../../../../components/Modal';
import TextField from '../../../../../../components/TextField';
import Button from '../../../../../../components/Button';
import Icon from '../../../../../../components/Icon';
import SearchableSelect from '../../../../../../components/SearchableSelect';
import { FiCalendar, FiMapPin, FiAlertCircle, FiCheckSquare, FiFileText, FiSave, FiUser, FiSearch, FiCheck } from 'react-icons/fi';
import { useAppSelector } from '../../../../../../store';
import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { fetchBranchBankId } from '../../../../../../screens/branches/service/branchesService';
import {
    generateSurveyId,
    submitNewLocationSurvey,
    assignNewLocationSurvey,
    type NewLocationSurveyFormData,
    type AssignLocationSurveyData
} from '../service/addNewLocationSurveyService';

type AddNewLocationSurveyModalProps = {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: (message?: string) => void;
};

export default function AddNewLocationSurveyModal({ isOpen, onClose, onSuccess }: AddNewLocationSurveyModalProps) {
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    const currentUser = useAppSelector((s) => s.user.currentUser);
    const psoProfile = useAppSelector((s) => s.user.pso);
    const [userDesignation, setUserDesignation] = useState<string>(psoProfile?.designation || '');
    const designation = useMemo(() => (userDesignation || '').replace(/_/g, ' '), [userDesignation]);
    const isMISUser = useMemo(() => designation.toLowerCase().includes('mis'), [designation]);
    const profileDesignation = psoProfile?.designation;
    const [resolvedBankId, setResolvedBankId] = useState<string | null>(null);
    const [bankResolutionError, setBankResolutionError] = useState<string | null>(null);

    const [surveyId, setSurveyId] = useState('');
    const [surveyRequestDate, setSurveyRequestDate] = useState('');
    const [surveyDate, setSurveyDate] = useState('');
    const [surveySubmittedOn, setSurveySubmittedOn] = useState('');
    const [locationAddress, setLocationAddress] = useState('');
    const [hazardousArea, setHazardousArea] = useState('');
    const [spaceConsistsOf, setSpaceConsistsOf] = useState('');

    const [directions, setDirections] = useState({
        front: '',
        left: '',
        back: '',
        right: ''
    });

    const [questions, setQuestions] = useState({
        fuelStationAvailable: '',
        spaceForParking: '',
        spaceForVantagePoint: '',
        spaceForEmergencyExit: '',
        electricityIssue: '',
        ptclLineAvailable: '',
        otherAssessment: 'No',
        otherAssessmentHazardous: 'No'
    });

    const [remarks, setRemarks] = useState('');

    const [psoOptions, setPsoOptions] = useState<Array<{ id: string; name: string; code?: string; designation?: string }>>([]);
    const [psoSearch, setPsoSearch] = useState('');
    const [selectedPso, setSelectedPso] = useState<{ id: string; name: string; code?: string } | null>(null);
    const [psosLoading, setPsosLoading] = useState(false);
    const [psosError, setPsosError] = useState<string | null>(null);

    const filteredPsoOptions = useMemo(() => {
        const term = psoSearch.trim().toLowerCase();
        if (!term) return psoOptions;
        return psoOptions.filter((pso) => {
            const nameMatch = pso.name?.toLowerCase().includes(term);
            const codeMatch = (pso.code || '').toLowerCase().includes(term);
            return nameMatch || codeMatch;
        });
    }, [psoOptions, psoSearch]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        setBankResolutionError(null);

        const resolveBank = async () => {
            try {
                const uid = auth.currentUser?.uid;
                if (!uid) {
                    throw new Error('User not authenticated');
                }

                const bankId = await fetchBranchBankId(uid);
                if (!bankId) {
                    throw new Error('Unable to determine bank');
                }

                setResolvedBankId(bankId);
            } catch (err: any) {
                console.error('[AddNewLocationSurveyModal] Failed to resolve bank ID:', err);
                setBankResolutionError(err.message || 'Unable to determine bank.');
                setResolvedBankId(null);
            }
        };

        resolveBank();
    }, [isOpen]);

    useEffect(() => {
        if (isOpen) {
            const today = new Date();
            const todayString = today.toISOString().split('T')[0];
            const sevenDaysLater = new Date(today);
            sevenDaysLater.setDate(today.getDate() + 7);

            const formatDate = (date: Date) => {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            };

            setSurveyId('');
            if (resolvedBankId) {
                const bankId = resolvedBankId;
                generateSurveyId(bankId)
                    .then(id => setSurveyId(id))
                    .catch(err => {
                        console.error('Failed to generate survey ID:', err);
                        setBankResolutionError('Unable to generate survey ID.');
                    });
            }
            setSurveySubmittedOn(todayString);
            setSurveyRequestDate(isMISUser ? todayString : todayString);
            setSurveyDate(todayString);
            setQuestions({
                fuelStationAvailable: '',
                spaceForParking: '',
                spaceForVantagePoint: '',
                spaceForEmergencyExit: '',
                electricityIssue: '',
                ptclLineAvailable: '',
                otherAssessment: 'No',
                otherAssessmentHazardous: 'No'
            });
            setHazardousArea('');
        } else if (!isOpen) {
            // Reset when modal closes
            setSurveyId('');
            setSurveyRequestDate('');
            setSurveyDate('');
            setSurveySubmittedOn('');
            setSelectedPso(null);
            setPsoSearch('');
            setPsosError(null);
            setBankResolutionError(null);
            setPsoOptions([]);
        }
    }, [isOpen, isMISUser, resolvedBankId]);

    useEffect(() => {
        if (!isOpen || !isMISUser) {
            return;
        }

        if (!resolvedBankId) {
            setPsoOptions([]);
            return;
        }

        const loadPsos = async () => {
            setPsosLoading(true);
            setPsosError(null);

            try {
                const psosSnap = await getDocs(collection(db, 'banks', resolvedBankId, 'pos'));
                const options: Array<{ id: string; name: string; code?: string; designation?: string }> = [];

                psosSnap.forEach((docSnap) => {
                    const data = docSnap.data() as Record<string, any>;
                    const designationRaw = (data.designation || '').toString().trim();
                    const normalized = designationRaw.toLowerCase();
                    const isPsoDesignation = normalized === 'pso' || normalized.startsWith('pso ');

                    if (isPsoDesignation) {
                        options.push({
                            id: docSnap.id,
                            name: data.name || 'Unnamed PSO',
                            code: data.code || data.employeeCode || data.psoCode || data.staffCode || '',
                            designation: designationRaw,
                        });
                    }
                });

                setPsoOptions(options);
                if (options.length === 0) {
                    setPsosError('No PSO profiles found for this bank.');
                }
            } catch (error) {
                console.error('[AddNewLocationSurveyModal] Failed to load PSOs:', error);
                setPsosError('Failed to load PSO profiles. Please try again.');
                setPsoOptions([]);
            } finally {
                setPsosLoading(false);
            }
        };

        loadPsos();
    }, [isOpen, isMISUser, resolvedBankId]);


    const handleDirectionChange = (direction: string, value: string) => {
        setDirections(prev => ({
            ...prev,
            [direction]: value
        }));
    };

    const handleQuestionChange = (question: string, value: string) => {
        setQuestions(prev => {
            const next = { ...prev, [question]: value };

            if (question === 'otherAssessment') {
                if (value === 'Yes') {
                    if (!next.otherAssessmentHazardous) {
                        next.otherAssessmentHazardous = 'No';
                    }
                } else {
                    next.otherAssessmentHazardous = 'No';
                    setHazardousArea('');
                }
            }

            if (question === 'otherAssessmentHazardous' && value !== 'Yes') {
                next.otherAssessmentHazardous = 'No';
            }

            return next;
        });
    };

    useEffect(() => {
        if (questions.otherAssessment !== 'Yes') {
            setHazardousArea('');
        }
    }, [questions.otherAssessment]);

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (isMISUser) {
            if (!selectedPso) {
                newErrors.selectedPso = 'Please select a PSO to assign this survey';
            }
            if (!locationAddress.trim()) {
                newErrors.locationAddress = 'Please enter location address';
            }
            setErrors(newErrors);
            return Object.keys(newErrors).length === 0;
        }

        if (!surveyRequestDate) {
            newErrors.surveyRequestDate = 'Please select survey request date';
        }
        if (!surveyDate) {
            newErrors.surveyDate = 'Please select survey date';
        }
        if (!locationAddress.trim()) {
            newErrors.locationAddress = 'Please enter location address';
        }
        if (questions.otherAssessment === 'Yes' && !hazardousArea.trim()) {
            newErrors.otherAssessmentDetails = 'Please provide assessment details';
        }
        if (!spaceConsistsOf) {
            newErrors.spaceConsistsOf = 'Please select space consists of';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) return;

        setLoading(true);
        try {
            if (isMISUser) {
                if (!selectedPso) {
                    throw new Error('Please select a PSO');
                }

                if (!resolvedBankId) {
                    setErrors({ submit: bankResolutionError || 'Bank information not available. Please try again later.' });
                    setLoading(false);
                    return;
                }

                const uid = auth.currentUser?.uid;
                if (!uid) {
                    throw new Error('User not authenticated');
                }

                const assignmentRequestDate = surveyRequestDate || new Date().toISOString().split('T')[0];

                await assignNewLocationSurvey({
                    surveyId,
                    assignedToPsoId: selectedPso.id,
                    assignedToPsoName: selectedPso.name,
                    assignedToPsoCode: selectedPso.code,
                    assignedBy: uid,
                    assignedByName: currentUser?.displayName || designation || 'MIS Incharge',
                    locationAddress: locationAddress.trim(),
                    bankId: resolvedBankId,
                    surveyRequestDate: assignmentRequestDate
                });

                setSelectedPso(null);
                setPsoSearch('');
                setLocationAddress('');
                setErrors({});
            } else {
                const formData: NewLocationSurveyFormData = {
                    surveyId,
                    surveyRequestDate,
                    surveyDate,
                    surveySubmittedOn,
                    locationAddress,
                    hazardousArea: questions.otherAssessment === 'Yes' ? hazardousArea : '',
                    spaceConsistsOf,
                    directions,
                    questions,
                    remarks
                };

                if (!resolvedBankId) {
                    setErrors({ submit: bankResolutionError || 'Bank information not available. Please try again later.' });
                    setLoading(false);
                    return;
                }

                await submitNewLocationSurvey(formData, resolvedBankId);

                // Reset form
                setSurveyRequestDate('');
                setSurveyDate('');
                setSurveySubmittedOn(new Date().toISOString().split('T')[0]);
                setLocationAddress('');
                setHazardousArea('');
                setSpaceConsistsOf('');
                setDirections({ front: '', left: '', back: '', right: '' });
                setQuestions({
                    fuelStationAvailable: '',
                    spaceForParking: '',
                    spaceForVantagePoint: '',
                    spaceForEmergencyExit: '',
                    electricityIssue: '',
                    ptclLineAvailable: '',
                    otherAssessment: 'No',
                    otherAssessmentHazardous: 'No'
                });
                setRemarks('');
                setErrors({});
            }

            if (resolvedBankId) {
                const bankId = resolvedBankId;
                generateSurveyId(bankId)
                    .then(id => setSurveyId(id))
                    .catch(err => {
                        console.error('Failed to regenerate survey ID:', err);
                        setBankResolutionError('Unable to generate survey ID.');
                    });
            }
            setSurveySubmittedOn(new Date().toISOString().split('T')[0]);

            onSuccess();
            onClose();
        } catch (err: any) {
            console.error('Error submitting survey:', err);
            setErrors({ submit: err.message || 'Failed to submit survey. Please try again.' });
        } finally {
            setLoading(false);
        }
    };

    const questionFields = [
        { key: 'fuelStationAvailable', label: 'Fuel Station Available Near Location?' },
        { key: 'spaceForParking', label: 'Space Available for Parking?' },
        { key: 'spaceForVantagePoint', label: 'Space Available for Vantage Point?' },
        { key: 'spaceForEmergencyExit', label: 'Space Available for Emergency Exit?' },
        { key: 'electricityIssue', label: 'Electricity Issue?' },
        { key: 'ptclLineAvailable', label: 'PTCL Line Available?' },
    ];

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="New Location Survey"
            size={isMISUser ? 'md' : 'xl'}
        >
            <form onSubmit={handleSubmit} className="p-6">
                <div className="space-y-6">
                    {bankResolutionError && (
                        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-300">
                            {bankResolutionError}
                        </div>
                    )}
                    <div>
                        <h3 className="mb-4 flex items-center gap-2 text-lg font-bold text-gray-900 dark:text-gray-100">
                            <Icon icon={FiCalendar} className="h-5 w-5" />
                            Survey Information
                        </h3>
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <div>
                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                    Survey No.
                                </label>
                                <TextField
                                    value={surveyId}
                                    readOnly
                                    className="bg-gray-50 dark:bg-gray-800"
                                />
                            </div>
                            {!isMISUser && (
                                <div>
                                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                        Survey Request Date <span className="text-red-500">*</span>
                                    </label>
                                    <TextField
                                        type="date"
                                        value={surveyRequestDate}
                                        onChange={(e) => setSurveyRequestDate(e.target.value)}
                                        required
                                    />
                                    {errors.surveyRequestDate && (
                                        <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.surveyRequestDate}</p>
                                    )}
                                </div>
                            )}
                            {!isMISUser && (
                                <div>
                                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                        Survey Date <span className="text-red-500">*</span>
                                    </label>
                                    <TextField
                                        type="date"
                                        value={surveyDate}
                                        min={new Date().toISOString().split('T')[0]}
                                        onChange={(e) => setSurveyDate(e.target.value)}
                                        required
                                    />
                                    {errors.surveyDate && (
                                        <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.surveyDate}</p>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {isMISUser ? (
                        <>
                            <div>
                                <h3 className="mb-3 flex items-center gap-2 text-lg font-bold text-gray-900 dark:text-gray-100">
                                    <Icon icon={FiUser} className="h-5 w-5" />
                                    Assign to PSO
                                </h3>
                                <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
                                    Select the PSO who will complete this survey. Only PSO designations are listed below.
                                </p>
                                <div className="space-y-3">
                                    <TextField
                                        value={psoSearch}
                                        onChange={(e) => setPsoSearch(e.target.value)}
                                        placeholder="Search PSO by name or code"
                                        prefix={<Icon icon={FiSearch} />}
                                        disabled={psosLoading}
                                    />
                                    <div className="max-h-60 overflow-y-auto rounded-xl border border-gray-200 dark:border-gray-700">
                                        {psosLoading ? (
                                            <div className="flex items-center justify-center py-6 text-sm text-gray-500 dark:text-gray-400">
                                                Loading PSO list...
                                            </div>
                                        ) : psosError ? (
                                            <div className="py-6 text-center text-sm text-red-600 dark:text-red-400">
                                                {psosError}
                                            </div>
                                        ) : filteredPsoOptions.length === 0 ? (
                                            <div className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                                                No PSO found for provided search.
                                            </div>
                                        ) : (
                                            <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                                                {filteredPsoOptions.map((pso) => {
                                                    const isActive = selectedPso?.id === pso.id;
                                                    return (
                                                        <li key={pso.id}>
                                                            <button
                                                                type="button"
                                                                onClick={() => setSelectedPso(pso)}
                                                                className={`flex w-full items-center justify-between px-4 py-3 text-left text-sm transition hover:bg-blue-50 dark:hover:bg-blue-900/20 ${isActive ? 'bg-blue-50 font-semibold text-blue-700 dark:bg-blue-900/30 dark:text-blue-200' : 'text-gray-700 dark:text-gray-200'}`}
                                                            >
                                                                <div>
                                                                    <div>{pso.name}</div>
                                                                    <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                                                        {(pso.code && `Code: ${pso.code}`) || 'No code provided'}
                                                                    </div>
                                                                </div>
                                                                {isActive && <Icon icon={FiCheck} className="h-4 w-4" />}
                                                            </button>
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        )}
                                    </div>
                                    {selectedPso && (
                                        <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800 dark:border-green-800/50 dark:bg-green-900/20 dark:text-green-200">
                                            <p className="font-semibold">Selected PSO</p>
                                            <p>{selectedPso.name}</p>
                                            {selectedPso.code && <p className="text-xs">Code: {selectedPso.code}</p>}
                                        </div>
                                    )}
                                    {errors.selectedPso && (
                                        <p className="text-xs text-red-600 dark:text-red-400">{errors.selectedPso}</p>
                                    )}
                                </div>
                            </div>

                            <div>
                                <h3 className="mb-3 flex items-center gap-2 text-lg font-bold text-gray-900 dark:text-gray-100">
                                    <Icon icon={FiMapPin} className="h-5 w-5" />
                                    Location Details
                                </h3>
                                <div className="mb-4">
                                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                        Request Date <span className="text-red-500">*</span>
                                    </label>
                                    <TextField
                                        type="date"
                                        value={surveyRequestDate}
                                        onChange={(e) => setSurveyRequestDate(e.target.value)}
                                        required
                                    />
                                </div>
                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                    Location Address <span className="text-red-500">*</span>
                                </label>
                                <textarea
                                    rows={3}
                                    value={locationAddress}
                                    onChange={(e) => setLocationAddress(e.target.value)}
                                    placeholder="Enter branch / site address"
                                    required
                                    className="w-full rounded-xl border py-3 px-4 text-sm outline-none transition bg-white text-gray-900 placeholder:text-gray-400 border-gray-300 shadow-sm focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-black/30 dark:text-gray-100 dark:border-gray-600"
                                />
                                {errors.locationAddress && (
                                    <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.locationAddress}</p>
                                )}
                            </div>
                        </>
                    ) : (
                        <>
                            {/* Directions Section */}
                            <div>
                                <h3 className="mb-4 flex items-center gap-2 text-lg font-bold text-gray-900 dark:text-gray-100">
                                    <Icon icon={FiMapPin} className="h-5 w-5" />
                                    Directions
                                </h3>
                                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                    <TextField
                                        label="Front"
                                        value={directions.front}
                                        onChange={(e) => handleDirectionChange('front', e.target.value)}
                                        placeholder="Enter direction details"
                                    />
                                    <TextField
                                        label="Left"
                                        value={directions.left}
                                        onChange={(e) => handleDirectionChange('left', e.target.value)}
                                        placeholder="Enter direction details"
                                    />
                                    <TextField
                                        label="Back"
                                        value={directions.back}
                                        onChange={(e) => handleDirectionChange('back', e.target.value)}
                                        placeholder="Enter direction details"
                                    />
                                    <TextField
                                        label="Right"
                                        value={directions.right}
                                        onChange={(e) => handleDirectionChange('right', e.target.value)}
                                        placeholder="Enter direction details"
                                    />
                                </div>
                            </div>

                            {/* Location Details Section */}
                            <div>
                                <h3 className="mb-4 flex items-center gap-2 text-lg font-bold text-gray-900 dark:text-gray-100">
                                    <Icon icon={FiMapPin} className="h-5 w-5" />
                                    Location Details
                                </h3>
                                <div className="space-y-4">
                                    <div>
                                        <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                            Location Address <span className="text-red-500">*</span>
                                        </label>
                                        <textarea
                                            rows={3}
                                            value={locationAddress}
                                            onChange={(e) => setLocationAddress(e.target.value)}
                                            placeholder="Enter complete address"
                                            required
                                            className="w-full rounded-xl border py-3 px-4 text-sm outline-none transition bg-white text-gray-900 placeholder:text-gray-400 border-gray-300 shadow-sm focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-black/30 dark:text-gray-100 dark:border-gray-600"
                                        />
                                        {errors.locationAddress && (
                                            <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.locationAddress}</p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Assessment Questions Section */}
                            <div>
                                <h3 className="mb-4 flex items-center gap-2 text-lg font-bold text-gray-900 dark:text-gray-100">
                                    <Icon icon={FiCheckSquare} className="h-5 w-5" />
                                    Assessment Questions
                                </h3>
                                <div className="space-y-4">
                                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                        {questionFields.map(({ key, label }) => (
                                            <div key={key} className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-black/30">
                                                <label className="mb-2 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                    {label}
                                                </label>
                                                <div className="flex gap-4">
                                                    <label className="flex items-center gap-2">
                                                        <input
                                                            type="radio"
                                                            name={key}
                                                            value="Yes"
                                                            checked={questions[key as keyof typeof questions] === 'Yes'}
                                                            onChange={(e) => handleQuestionChange(key, e.target.value)}
                                                            className="h-4 w-4"
                                                        />
                                                        <span className="text-sm">Yes</span>
                                                    </label>
                                                    <label className="flex items-center gap-2">
                                                        <input
                                                            type="radio"
                                                            name={key}
                                                            value="No"
                                                            checked={questions[key as keyof typeof questions] === 'No'}
                                                            onChange={(e) => handleQuestionChange(key, e.target.value)}
                                                            className="h-4 w-4"
                                                        />
                                                        <span className="text-sm">No</span>
                                                    </label>
                                                </div>
                                            </div>
                                        ))}
                                        <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-black/30">
                                            <label className="mb-2 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                Any Other Assessment?
                                            </label>
                                            <div className="mb-3 flex gap-4 text-sm">
                                                <label className="flex items-center gap-2">
                                                    <input
                                                        type="radio"
                                                        name="otherAssessment"
                                                        value="Yes"
                                                        checked={questions.otherAssessment === 'Yes'}
                                                        onChange={(e) => handleQuestionChange('otherAssessment', e.target.value)}
                                                        className="h-4 w-4"
                                                    />
                                                    Yes
                                                </label>
                                                <label className="flex items-center gap-2">
                                                    <input
                                                        type="radio"
                                                        name="otherAssessment"
                                                        value="No"
                                                        checked={questions.otherAssessment === 'No' || questions.otherAssessment === ''}
                                                        onChange={(e) => handleQuestionChange('otherAssessment', e.target.value)}
                                                        className="h-4 w-4"
                                                    />
                                                    No
                                                </label>
                                            </div>
                                            {questions.otherAssessment === 'Yes' && (
                                                <div className="space-y-3">
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                                                        Assessment Details
                                                    </label>
                                                    <textarea
                                                        rows={3}
                                                        value={hazardousArea}
                                                        onChange={(e) => setHazardousArea(e.target.value)}
                                                        placeholder="Describe the assessment"
                                                        className="w-full rounded-xl border py-3 px-4 text-sm outline-none transition bg-white text-gray-900 placeholder:text-gray-400 border-gray-300 shadow-sm focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-black/30 dark:text-gray-100 dark:border-gray-600"
                                                    />
                                                    {errors.otherAssessmentDetails && (
                                                        <p className="text-xs text-red-600 dark:text-red-400">{errors.otherAssessmentDetails}</p>
                                                    )}
                                                    <div>
                                                        <span className="mb-2 block text-sm font-semibold text-gray-700 dark:text-gray-200">Is it hazardous?</span>
                                                        <div className="flex gap-4 text-sm">
                                                            <label className="flex items-center gap-2">
                                                                <input
                                                                    type="radio"
                                                                    name="otherAssessmentHazardous"
                                                                    value="Yes"
                                                                    checked={questions.otherAssessmentHazardous === 'Yes'}
                                                                    onChange={(e) => handleQuestionChange('otherAssessmentHazardous', e.target.value)}
                                                                    className="h-4 w-4"
                                                                />
                                                                Yes
                                                            </label>
                                                            <label className="flex items-center gap-2">
                                                                <input
                                                                    type="radio"
                                                                    name="otherAssessmentHazardous"
                                                                    value="No"
                                                                    checked={questions.otherAssessmentHazardous === 'No' || questions.otherAssessmentHazardous === ''}
                                                                    onChange={(e) => handleQuestionChange('otherAssessmentHazardous', e.target.value)}
                                                                    className="h-4 w-4"
                                                                />
                                                                No
                                                            </label>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Space Details Section */}
                            <div>
                                <h3 className="mb-4 flex items-center gap-2 text-lg font-bold text-gray-900 dark:text-gray-100">
                                    <Icon icon={FiAlertCircle} className="h-5 w-5" />
                                    Space Details
                                </h3>
                                <div>
                                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                        Space Consists Of <span className="text-red-500">*</span>
                                    </label>
                                    <SearchableSelect
                                        value={spaceConsistsOf}
                                        onChange={(value: string) => setSpaceConsistsOf(value)}
                                        options={[
                                            { id: 'space-select', label: 'Select option', value: '' },
                                            { id: 'space-ground-only', label: 'Ground Only', value: 'Ground Only' },
                                            { id: 'space-ground-basement', label: 'Ground + Basement', value: 'Ground + Basement' },
                                            { id: 'space-ground-basement-first', label: 'Ground + Basement + First Floor', value: 'Ground + Basement + First Floor' },
                                            { id: 'space-ground-first', label: 'Ground + First Floor', value: 'Ground + First Floor' },
                                            { id: 'space-ground-first-second', label: 'Ground + First Floor + Second Floor', value: 'Ground + First Floor + Second Floor' },
                                            { id: 'space-multi-story', label: 'Multi-story Building', value: 'Multi-story Building' },
                                            { id: 'space-commercial', label: 'Commercial Complex', value: 'Commercial Complex' },
                                            { id: 'space-industrial', label: 'Industrial Area', value: 'Industrial Area' },
                                            { id: 'space-residential', label: 'Residential Complex', value: 'Residential Complex' },
                                            { id: 'space-other', label: 'Other', value: 'Other' }
                                        ]}
                                    />
                                    {errors.spaceConsistsOf && (
                                        <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.spaceConsistsOf}</p>
                                    )}
                                </div>
                            </div>

                            {/* Remarks Section */}
                            <div>
                                <h3 className="mb-4 flex items-center gap-2 text-lg font-bold text-gray-900 dark:text-gray-100">
                                    <Icon icon={FiFileText} className="h-5 w-5" />
                                    Remarks
                                </h3>
                                <div>
                                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                        Remarks
                                    </label>
                                    <textarea
                                        rows={4}
                                        value={remarks}
                                        onChange={(e) => setRemarks(e.target.value)}
                                        placeholder="Enter any additional remarks"
                                        className="w-full rounded-xl border py-3 px-4 text-sm outline-none transition bg-white text-gray-900 placeholder:text-gray-400 border-gray-300 shadow-sm focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-black/30 dark:text-gray-100 dark:border-gray-600"
                                    />
                                </div>
                            </div>
                        </>
                    )}

                    {errors.submit && (
                        <div className="rounded-lg bg-red-50 p-3 text-sm text-red-600 dark:bg-red-500/10 dark:text-red-400">
                            {errors.submit}
                        </div>
                    )}
                </div>

                <div className="mt-6 flex gap-3">
                    <Button
                        type="button"
                        variant="secondary"
                        onClick={onClose}
                        className="flex-1"
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        className="flex-1"
                        disabled={loading}
                    >
                        <Icon icon={FiSave} className="mr-2 h-4 w-4" />
                        {loading ? 'Submitting...' : isMISUser ? 'Assign Survey' : 'Submit Survey'}
                    </Button>
                </div>
            </form>
        </Modal>
    );
}
